/**
 * providers/UserProvider.tsx
 *
 * User context — stores the full user profile after authentication.
 * isLoggedIn is derived purely from the user state — no cookie check in JS
 * since user_token is now an httpOnly cookie (not readable by JS).
 *
 * Session restore happens in layout via /api/auth/refresh.
 */

'use client';

import React, { createContext, ReactNode, useContext, useState } from 'react';
import { checkIfValidUser } from '@/helpers/utils';
import {
  clearCachedDisplay,
  getCachedDisplay,
  setCachedDisplay,
} from '@/helpers/auth/displayCache';

export interface UserInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
  category: string;
  verificationStatus: boolean;
}

interface UserContextType {
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;
  isLoggedIn: boolean;
  cachedInitials: string;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUserState] = useState<UserInfo | null>(null);
  const [cachedInitials, setCachedInitials] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return getCachedDisplay()?.initials ?? '';
  });

  const setUser = (newUser: UserInfo | null) => {
    setUserState(newUser);
    if (newUser && checkIfValidUser(newUser)) {
      setCachedDisplay(newUser);
      setCachedInitials(
        (newUser.firstName?.[0] ?? '').toUpperCase() +
          (newUser.lastName?.[0] ?? '').toUpperCase(),
      );
    } else {
      clearCachedDisplay();
      setCachedInitials('');
    }
  };

  const isLoggedIn = !!(user && checkIfValidUser(user));

  return (
    <UserContext.Provider value={{ user, setUser, isLoggedIn, cachedInitials }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    if (typeof window === 'undefined') {
      return {
        user: null,
        setUser: () => {},
        isLoggedIn: false,
        cachedInitials: '',
      };
    }
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};
